import * as Sentry from '@sentry/react'
import { useEffect, useRef, useState } from 'react'

import { getComparatorSampleCollected, type ComparatorSampleCollectedResponse } from '@/utils/DatabaseOperations'

export interface UseComparatorSampleCollectedOptions {
  /** The submitted order's UUID. When absent (e.g. a draft), the check is skipped. */
  orderId?: string | null
  /**
   * The person's IMDB patient/member id (``demographic.demographicId``), or the
   * ``"proband"`` sentinel (``PROBAND_SAMPLE_COLLECTED_SENTINEL``) to check the primary
   * patient. When absent, the check is skipped.
   */
  patientId?: string | null
  /**
   * Gate the network call. Pass ``false`` to skip entirely (e.g. the order is not in a
   * submitted state where sample collection is meaningful). Defaults to ``true``.
   */
  enabled?: boolean
}

export type ComparatorSampleCollectedStatus = 'idle' | 'loading' | 'loaded' | 'error'

export interface UseComparatorSampleCollectedResult {
  status: ComparatorSampleCollectedStatus
  /** The full diagnostic payload once loaded (specimen/beaker/redox breakdown). */
  data: ComparatorSampleCollectedResponse | null
  /**
   * Whether the order's organization is Redox-enabled, once known. ``null`` until the
   * check has loaded (loading / error / idle). The gate only engages for Redox orgs —
   * see ``canRemove`` — so this is exposed for copy that distinguishes "not a Redox org"
   * from "sample not yet collected".
   */
  isRedoxEnabled: boolean | null
  /**
   * Redox-scoped, fail-closed action predicate. ``true`` (action allowed) when:
   *   - the check is not applicable (disabled / no order id — e.g. a draft), OR
   *   - the loaded response says the org is NOT Redox-enabled (the sample-collection
   *     concept doesn't apply, so nothing is gated), OR
   *   - the loaded response definitively reports the sample was NOT collected.
   *
   * ``false`` (action blocked) while loading, on error, or on a confirmed collected
   * sample. Because ``sampleCollected`` is only ever ``true`` for Redox orgs, the block
   * only ever engages for Redox-enabled organizations; non-Redox orgs are freely
   * actionable as soon as the check resolves. The brief loading/error window stays
   * fail-closed since the org's Redox status isn't known yet.
   */
  canRemove: boolean
}

/**
 * Redox-scoped, fail-closed sample-collection gate for a person on a submitted order.
 *
 * Calls ``GET /ordering/orders/{orderId}/comparators/{patientId}/sample-collected`` (where
 * ``patientId`` is a comparator's ``demographic.demographicId`` or the ``"proband"`` sentinel
 * for the primary patient) and exposes a conservative ``canRemove`` predicate. The gate only
 * engages for Redox-enabled organizations: ``sampleCollected`` is defined server-side as
 * ``(specimenPresent || beakerPresent) && redoxEnabled``, so a non-Redox org always reports
 * ``sampleCollected: false`` and is never blocked. The gated action is permitted once the
 * backend confirms either the org is not Redox-enabled or the sample was not collected.
 * Loading and error resolve to blocked, so a lab-collected person can never be actioned on an
 * optimistic UI while the check is in flight or has failed.
 *
 * A 404 (order not found / not in the caller's org) is surfaced as ``error`` and therefore
 * remains blocked. The backend still enforces the authoritative gate on the mutation.
 */
export function useComparatorSampleCollected({
  orderId,
  patientId,
  enabled = true,
}: UseComparatorSampleCollectedOptions): UseComparatorSampleCollectedResult {
  const trimmedOrderId = orderId?.trim() ?? ''
  const trimmedPatientId = patientId?.trim() ?? ''
  const shouldCheck = enabled && trimmedOrderId.length > 0 && trimmedPatientId.length > 0

  const [status, setStatus] = useState<ComparatorSampleCollectedStatus>('idle')
  const [data, setData] = useState<ComparatorSampleCollectedResponse | null>(null)
  const requestIdRef = useRef(0)
  // Identifies the inputs the current `status`/`data` describe. When the inputs
  // change, the async effect (which resets + refetches) hasn't run yet — it fires
  // after this render commits — so trusting the existing `status`/`data` here would
  // expose the *previous* comparator's result for one render. Switching from an
  // uncollected to a collected comparator would then briefly leave removal enabled,
  // breaking the fail-closed gate. Reset synchronously during render (React's
  // supported "adjust state when a prop changes" pattern).
  //
  // The previous key is tracked in STATE, not a ref: under concurrent React a render
  // can be discarded and retried, which would keep a ref mutation but drop the paired
  // `setState` calls — the retry would then see a matching key and skip the reset. A
  // state setter called during render is discarded and re-applied atomically with the
  // render, so the reset can't be lost.
  const inputKey = shouldCheck ? `${trimmedOrderId}\u0000${trimmedPatientId}` : ''
  const [checkedKey, setCheckedKey] = useState<string | null>(null)
  if (checkedKey !== inputKey) {
    setCheckedKey(inputKey)
    const resetStatus: ComparatorSampleCollectedStatus = shouldCheck ? 'loading' : 'idle'
    if (status !== resetStatus) setStatus(resetStatus)
    if (data !== null) setData(null)
  }

  useEffect(() => {
    if (!shouldCheck) {
      setStatus('idle')
      setData(null)
      return
    }

    const controller = new AbortController()
    const requestId = ++requestIdRef.current
    setStatus('loading')
    setData(null)

    void (async () => {
      try {
        const result = await getComparatorSampleCollected(trimmedOrderId, trimmedPatientId, controller.signal)
        if (requestId !== requestIdRef.current) return
        if (result == null) {
          // 404: order not found / not in org. Fail closed (blocked).
          setStatus('error')
          setData(null)
          return
        }
        setStatus('loaded')
        setData(result)
      } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') return
        if (requestId !== requestIdRef.current) return
        Sentry.captureException(e, {
          tags: { operation: 'ordering.comparator_sample_collected' },
        })
        setStatus('error')
        setData(null)
      }
    })()

    return () => {
      controller.abort()
    }
  }, [shouldCheck, trimmedOrderId, trimmedPatientId])

  // Because the render-phase reset above realigns `status`/`data` with the current
  // inputs before commit, these read the fresh (non-stale) values. Redox-scoped,
  // fail closed: allowed when the check is not applicable (disabled / no order id),
  // or the loaded response says the org is not Redox-enabled (nothing to gate), or
  // the sample was definitively not collected. Loading/error stay blocked because
  // the org's Redox status isn't known yet.
  const isRedoxEnabled = status === 'loaded' && data ? data.redoxEnabled === true : null
  const canRemove =
    !shouldCheck || (status === 'loaded' && (data?.redoxEnabled === false || data?.sampleCollected === false))

  return { status, data, isRedoxEnabled, canRemove }
}
